// Top-level error boundary. Catches anything thrown during render so the
// operator gets a recoverable screen instead of a white page. Reports the
// error to the client_errors sink for triage.
//
// Stale-bundle recovery: when the error matches a ChunkLoadError pattern
// (the unambiguous "your cached SW chunk is broken" signal), we
// automatically run the SW recovery path. Otherwise we still expose a
// manual "Reset app" button so the operator can recover without
// DevTools when a normal Reload doesn't help.

import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, Home, RefreshCw, Trash2, WifiOff } from 'lucide-react'
import { reportError } from '@/lib/errorReporter'
import { recoverFromStaleBundle, autoRecoverStaleBundle, looksLikeStaleBundle } from '@/lib/swRecovery'

interface Props {
  children: ReactNode
}

interface State {
  error:        Error | null
  info:         ErrorInfo | null
  autoRecovering: boolean
  resetting:    boolean
}

// Session-storage flag — guards us against an auto-recover loop. If we
// already auto-reloaded for a stale bundle in this tab and the error
// fires again, surface the manual buttons instead of looping forever.
const AUTO_RECOVER_KEY = 'kf49.errorboundary.autorecovered'

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null, autoRecovering: false, resetting: false }

  // URL (path + search) at the moment of the crash — see onRouteChange.
  private erroredAt: string | null = null

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  componentDidMount(): void {
    if (typeof window !== 'undefined') window.addEventListener('popstate', this.onRouteChange)
  }

  componentWillUnmount(): void {
    if (typeof window !== 'undefined') window.removeEventListener('popstate', this.onRouteChange)
  }

  // One crashed page must not pin every LATER navigation on the error card
  // (render-10: a single seeded crash left 10 subsequent routes dead until a
  // hard reload). While the card shows, the router below us is unmounted, so
  // the only route changes that can still happen are history traversals —
  // the popstate event, the same signal BrowserRouter itself navigates on.
  // When one lands on a DIFFERENT url, clear the error so the remounted
  // router renders the new route. A same-url traversal (hash-only change)
  // stays on the card rather than re-throw + re-report in a loop — "Try
  // again" covers the deliberate same-route retry.
  private onRouteChange = () => {
    if (!this.state.error) return
    const here = window.location.pathname + window.location.search
    if (here !== this.erroredAt) this.reset()
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.erroredAt = typeof location !== 'undefined'
      ? location.pathname + location.search
      : null
    this.setState({ info })
    void reportError(error, {
      source:  'boundary',
      context: { componentStack: info.componentStack },
    })

    // Auto-recover from stale-bundle crashes (ChunkLoadError + friends).
    // Only attempt once per session — if a reload still throws the same
    // error, fall back to showing the manual recovery buttons.
    //
    // Uses the SAFE auto path (autoRecoverStaleBundle): it only activates a
    // waiting SW + reloads, and NEVER nukes the SW/caches. Critically, the same
    // "failed to fetch dynamically imported module" fires when a chunk simply
    // isn't reachable offline — and auto-nuking there would delete the device's
    // only copy of the app (the bug behind "every module → blank offline"). If
    // there's nothing safe to do (offline, or no waiting update), we drop back
    // to the error UI; nuking stays behind the explicit "Reset app" button.
    const alreadyTried = (() => {
      try { return sessionStorage.getItem(AUTO_RECOVER_KEY) === '1' } catch { return false }
    })()
    if (!alreadyTried && looksLikeStaleBundle(error)) {
      try { sessionStorage.setItem(AUTO_RECOVER_KEY, '1') } catch { /* ignore */ }
      this.setState({ autoRecovering: true })
      void autoRecoverStaleBundle().then(started => {
        // Nothing safe to activate (offline / no waiting update) → don't nuke;
        // show the error UI with the manual Reset button instead.
        if (!started) this.setState({ autoRecovering: false })
      })
    }
  }

  reset = () => this.setState({ error: null, info: null })

  reload = () => {
    if (typeof location !== 'undefined') location.reload()
  }

  // Recovery that depends on neither the history stack nor the crashed route
  // rendering cleanly a second time: move the URL to Home FIRST, then clear
  // the error so the remounted router renders "/". Stays in-SPA (no reload),
  // so it also works offline where fetching a fresh document may not.
  goHome = () => {
    try { history.pushState(null, '', '/') } catch { /* ignore */ }
    this.reset()
  }

  // Nuclear recovery — unregisters SWs + nukes Cache Storage + reloads.
  // Session, localStorage, IndexedDB (offline queue) are preserved.
  resetApp = async () => {
    this.setState({ resetting: true })
    try { sessionStorage.removeItem(AUTO_RECOVER_KEY) } catch { /* ignore */ }
    await recoverFromStaleBundle()
  }

  render() {
    if (!this.state.error) return this.props.children

    const isProd = import.meta.env.PROD
    const msg    = this.state.error.message || 'Unknown error'
    const stack  = this.state.error.stack ?? ''
    const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false

    // Offline: the usual cause here is a module whose code chunk hasn't been
    // cached yet. Do NOT offer "Reset app" — offline it deletes the saved app
    // (and is a no-op for getting fresh code). Give a calm, accurate message
    // instead, and a safe way back.
    if (isOffline) {
      return (
        <div className="min-h-full surface-sunk flex items-center justify-center p-6">
          <div className="surface rounded-2xl p-6 max-w-md w-full text-center">
            <WifiOff size={36} className="text-amber-400 mx-auto mb-3" />
            <h1 className="text-lg font-bold text-fg mb-1">You're offline</h1>
            <p className="text-sm text-fg-muted mb-4">
              This section hasn't been saved for offline use yet. Open it once while
              you have signal and it'll work offline after that. Your other screens
              and any saved work are safe.
            </p>
            <div className="flex gap-2 justify-center flex-wrap">
              <button
                onClick={() => { this.reset(); if (typeof history !== 'undefined') history.back() }}
                className="btn-primary flex items-center gap-1.5">
                ← Go back
              </button>
              <button onClick={this.reset} className="btn-ghost">Try again</button>
            </div>
          </div>
        </div>
      )
    }

    if (this.state.autoRecovering) {
      return (
        <div className="min-h-full surface-sunk flex items-center justify-center p-6">
          <div className="surface rounded-2xl p-6 max-w-md w-full text-center">
            <RefreshCw size={36} className="text-blue-400 mx-auto mb-3 animate-spin" />
            <h1 className="text-lg font-bold text-fg mb-1">Loading the latest version…</h1>
            <p className="text-sm text-fg-muted">
              Your tab was running an older build. We're refreshing to the current one.
            </p>
          </div>
        </div>
      )
    }

    return (
      <div className="min-h-full surface-sunk flex items-center justify-center p-6">
        <div className="surface rounded-2xl p-6 max-w-lg w-full text-center">
          <AlertTriangle size={36} className="text-amber-500 mx-auto mb-3" />
          <h1 className="text-lg font-bold text-fg mb-1">Something went wrong</h1>
          <p className="text-sm text-fg-muted mb-4">
            The error has been logged. Reload usually fixes it. If it keeps happening,
            tap "Reset app" — that clears the cached bundle and pulls a fresh copy.
          </p>

          <div className="flex gap-2 justify-center flex-wrap">
            <button
              onClick={this.reload}
              disabled={this.state.resetting}
              className="btn-primary flex items-center gap-1.5">
              <RefreshCw size={14} /> Reload
            </button>
            <button
              onClick={this.goHome}
              disabled={this.state.resetting}
              className="btn-ghost flex items-center gap-1.5">
              <Home size={14} /> Go to Home
            </button>
            <button
              onClick={this.reset}
              disabled={this.state.resetting}
              className="btn-ghost">
              Try again
            </button>
            <button
              onClick={this.resetApp}
              disabled={this.state.resetting}
              className="btn-secondary flex items-center gap-1.5 text-amber-300"
              title="Unregister the cached service worker and reload">
              <Trash2 size={14} /> {this.state.resetting ? 'Resetting…' : 'Reset app'}
            </button>
          </div>

          <p className="text-[11px] text-fg-muted mt-3">
            Reset app keeps your sign-in and offline queue. It just clears the
            cached app bundle so you get the latest version.
          </p>

          {!isProd && (
            <details className="mt-4 text-left surface-sunk rounded-lg p-3 text-xs text-fg-muted">
              <summary className="cursor-pointer text-fg-secondary font-semibold">
                Developer details
              </summary>
              <p className="mt-2 font-mono text-amber-300">{msg}</p>
              {stack && (
                <pre className="mt-2 overflow-x-auto text-[10px] text-fg-muted whitespace-pre-wrap">
                  {stack}
                </pre>
              )}
            </details>
          )}
        </div>
      </div>
    )
  }
}
